// savedSearchesApi — thin wrapper around the shared axios `api` instance for
// the tender search "saved searches" endpoints. Same conventions as
// tendersApi.js: `api` already injects the Bearer token, and `withOrg` scopes
// every call to the signed-in user's organisation.
//
// A saved search is private to the person who made it — the backend 404s a
// row that belongs to someone else, even a colleague in the same workspace,
// rather than 403ing, so its existence is never leaked either.
import Cookies from "js-cookie";

import api from "../pages/chat/api";

const BASE = "/api/v1/tenders/saved-searches/";

const withOrg = (params = {}) => {
  const orgId = Cookies.get("selected_org_id");
  return orgId ? { organization_id: orgId, ...params } : { ...params };
};

// GET /api/v1/tenders/saved-searches/ -> [{ id, name, state, alert_email,
//   alert_frequency, alert_inapp, skip_closing_within_days, last_viewed_at,
//   last_digest_sent_at, new_count, created_at, updated_at }, ...]
//
// `new_count` is computed fresh on every call, never cached — the same rule
// every other count on this search page holds to.
export const listSavedSearches = async ({ signal } = {}) => {
  const res = await api.get(BASE, { params: withOrg(), signal });
  return res.data;
};

// GET /api/v1/tenders/saved-searches/<id>/ -> one row, for links that name a saved search
export const getSavedSearch = async (savedSearchId) => {
  const res = await api.get(`${BASE}${savedSearchId}/`, { params: withOrg() });
  return res.data;
};

// POST /api/v1/tenders/saved-searches/ -> the created row
//
// `state` is exactly `{ keywords, ordering, filters }` — the same shape
// TenderSearch.jsx already holds in memory (see TenderFilters.jsx's
// EMPTY_FILTERS). Nothing here re-encodes it as a querystring.
export const createSavedSearch = async ({
  name, state, alertEmail = false, alertFrequency = "off",
}) => {
  const res = await api.post(BASE, {
    name,
    state,
    alert_email: alertEmail,
    alert_frequency: alertFrequency,
  }, { params: withOrg() });
  return res.data;
};

// PATCH /api/v1/tenders/saved-searches/<id>/ -> the updated row
//
// Partial by design: rename alone, "Update search" (new `state` alone), or an
// alert-settings change alone all go through this one call.
export const updateSavedSearch = async (savedSearchId, patch) => {
  const res = await api.patch(`${BASE}${savedSearchId}/`, patch, { params: withOrg() });
  return res.data;
};

// DELETE /api/v1/tenders/saved-searches/<id>/
export const deleteSavedSearch = async (savedSearchId) => {
  await api.delete(`${BASE}${savedSearchId}/`, { params: withOrg() });
};

// POST /api/v1/tenders/saved-searches/<id>/view/
//
// Bumps the pivot the in-app "N new" badge counts from. Called when a saved
// search is actually opened — never from listing the rail, which must not
// itself clear a badge nobody has looked at yet.
export const markSavedSearchViewed = async (savedSearchId) => {
  await api.post(`${BASE}${savedSearchId}/view/`, {}, { params: withOrg() });
};
